/**
 * Mieterauswahl im Sheet „Zahlung erfassen" (`payment-create-tenant`, Spec
 * 04b, „Sheet Zahlung erfassen"). Nur beim Einstieg **ohne Bezug**
 * (`payment-add` im Bereich *Geld*) gerendert — kommt das Sheet aus einem
 * offenen Posten, steht der Mieter fest und dieses Feld entfällt ganz
 * (Feldtabelle: „entfällt, wenn er feststeht").
 *
 * Die Mieterauswahl folgt Spec 03 (`contract-tenant-*`): dieselben inneren
 * Bezeichner wie `TenantPicker` (`features/contracts`), hier noch einmal
 * lokal, weil `features/*` einander nicht kennen dürfen — dieselbe
 * Duplikation wie `TenantFilterField` (Spec 04a, hier im selben Ordner).
 * Anders als der Filter **kein** „Neuen Mieter anlegen": Eine Zahlung von
 * einem Mieter, den es nicht gibt, ist ein Erfassungsfehler, kein Anlass für
 * einen neuen Datensatz (Spec 04b).
 */

import { useState } from 'react'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { useTenantOptions } from '@/features/billing/api'
import { FIELD_CLASS } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

export interface PaymentTenantSelection {
  readonly id: string
  readonly label: string
}

interface PaymentTenantFieldProps {
  readonly value: PaymentTenantSelection | null
  readonly onSelect: (tenant: PaymentTenantSelection) => void
  readonly error: FieldError | undefined
}

const API_FIELD = 'tenant_id'

export function PaymentTenantField({ value, onSelect, error }: PaymentTenantFieldProps) {
  const [q, setQ] = useState('')
  const [isSearching, setIsSearching] = useState(value === null)
  // Ein von außen gesetzter Mieter schließt die Suche — während des Renderns
  // verglichen (React-Muster „Adjusting state when a prop changes"), wie
  // `TenantPicker`.
  const [syncedValueId, setSyncedValueId] = useState(value?.id)
  if (value?.id !== syncedValueId) {
    setSyncedValueId(value?.id)
    setIsSearching(value === null)
    setQ('')
  }

  const optionsQuery = useTenantOptions(q, { enabled: isSearching })
  const hasError = error?.field === API_FIELD
  const errorTestId = `payment-create-error-${API_FIELD}`

  return (
    <div className="flex flex-col gap-1" data-testid="payment-create-tenant">
      <span className="text-label text-muted">Mieter</span>

      {value !== null && !isSearching ? (
        <div className={`flex items-center justify-between gap-2 ${FIELD_CLASS}`}>
          <span className="text-text">{value.label}</span>
          <button
            type="button"
            data-testid="payment-create-tenant-change"
            onClick={() => {
              setIsSearching(true)
            }}
            className="text-accent text-label inline-flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center hover:underline"
          >
            Ändern
          </button>
        </div>
      ) : (
        <input
          data-testid="contract-tenant-search"
          type="search"
          aria-label="Mieter suchen"
          placeholder="Mieter suchen"
          value={q}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorTestId : undefined}
          onChange={(event) => {
            setQ(event.target.value)
          }}
          className={`text-text ${FIELD_CLASS}`}
        />
      )}

      {(value === null || isSearching) && q.trim() !== '' && optionsQuery.isPending && (
        <SkeletonRows testId="contract-tenant-loading" rows={3} />
      )}

      {(value === null || isSearching) && q.trim() !== '' && optionsQuery.isError && (
        <ErrorState
          testId="contract-tenant-error"
          retryTestId="contract-tenant-retry"
          error={optionsQuery.error}
          onRetry={() => {
            void optionsQuery.refetch()
          }}
        />
      )}

      {(value === null || isSearching) && q.trim() !== '' && optionsQuery.isSuccess && optionsQuery.data.items.length === 0 && (
        <EmptyState testId="contract-tenant-empty" title="Kein Mieter gefunden." />
      )}

      {(value === null || isSearching) && q.trim() !== '' && optionsQuery.isSuccess && optionsQuery.data.items.length > 0 && (
        <ul className="border-border rounded-card border">
          {optionsQuery.data.items.map((tenant) => (
            <li key={tenant.id} className="border-border border-b last:border-b-0">
              <button
                type="button"
                data-testid={`contract-tenant-option-${tenant.id}`}
                onClick={() => {
                  onSelect({ id: tenant.id, label: tenant.label })
                  setIsSearching(false)
                  setQ('')
                }}
                className="hover:bg-bg flex min-h-(--tap-min) w-full items-center px-3 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
              >
                <span className="text-body text-text">{tenant.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {hasError && (
        <p id={errorTestId} data-testid={errorTestId} role="alert" className="text-status-overdue">
          {error.message}
        </p>
      )}
    </div>
  )
}
